import {
  rundeAnordnung,
  rundeGroesse,
  rundeVersatz,
  type Anordnung,
} from "../../shared/anordnung.ts";
import { ueberlappungenAusBreiten, UEBERLAPP_ANTEIL } from "./reihe.ts";

/**
 * Die eine Reihe mit Bodenlinie — das Rechnen dahinter (Schritt 4, ARCHITEKTUR.md 6.2).
 *
 * Alles hier ist **rein**: Zahlen rein, Zahlen raus, kein DOM, kein React — dieselbe
 * Trennung wie in `src/lib/umriss.ts`. Das ist der Teil, der still falsch sein kann: eine
 * Reihe, deren linke Kanten um wenige Pixel verrutschen, sieht richtig aus und setzt
 * eine Figur in eine Lücke, die Anna später sucht.
 *
 * Die Grenzen und die Prüfung stehen in `shared/anordnung.ts`, weil der Server sie
 * ebenfalls braucht.
 */

/** Wie breit eine Einfügemarke mindestens ist. Die 44 px sind die Trefferfläche aus
 *  ARCHITEKTUR.md 2.3 — eine Marke, die man nicht trifft, ist keine. */
export const MARKE_BREITE_PX = 44;

/** Eine Figur, wie die Oberfläche sie hält: mit ihrem Seitenverhältnis aus dem Umriss. */
export interface Figur {
  bildId: string;
  /** Breite zu Höhe des **Hüllrechtecks**, aus `ausschnitt()` in `src/lib/umriss.ts`. */
  seitenverhaeltnis: number;
  groesse: number;
  versatz: number;
}

/** Eine Figur mit ihrem Platz in Pixeln. `links` ist die linke Kante in der Leiste. */
export interface GelegteFigur {
  bildId: string;
  links: number;
  breite: number;
  hoehe: number;
  /** Wie weit die Figur über der Bodenlinie hängt. **Negativ heißt: unter die Linie
   *  gesenkt** — der gedachte Fall bei angeschnittenen Beinen. */
  hebung: number;
}

export interface Reihenlayout {
  figuren: GelegteFigur[];
  /** Gesamtbreite der Leiste in px. Sie bestimmt, wie weit waagerecht gescrollt wird. */
  breite: number;
  /** Was die Reihe über der Bodenlinie braucht — die höchste Figur plus ihre Hebung. */
  hoehe: number;
}

/**
 * Wo jede Figur liegt.
 *
 * Die waagerechte Lage folgt aus der **Reihenfolge**, nicht aus einer Koordinate
 * (6.5): jede Figur beginnt dort, wo die vorherige endet, minus der Überlappung. Die
 * senkrechte folgt aus der Bodenlinie: alle Unterkanten sitzen auf derselben Linie,
 * verschoben nur um die eigene Hebung.
 */
export function reihenlayout(
  figuren: readonly Figur[],
  kachelHoehe: number,
  anteil: number = UEBERLAPP_ANTEIL,
): Reihenlayout {
  const hoehen = figuren.map((figur) => Math.max(0, kachelHoehe * figur.groesse));
  const breiten = figuren.map((figur, i) => {
    const hoehe = hoehen[i] ?? 0;
    const sv = figur.seitenverhaeltnis;
    // Ein unbrauchbares Seitenverhältnis — 0, negativ, NaN — ergibt keine Breite und
    // keine Rechnung mit NaN, die sich durch die ganze Reihe zieht.
    return Number.isFinite(sv) && sv > 0 ? hoehe * sv : 0;
  });
  const ueberlapp = ueberlappungenAusBreiten(breiten, anteil);

  let kante = 0;
  const gelegt = figuren.map((figur, i) => {
    const breite = breiten[i] ?? 0;
    const hoehe = hoehen[i] ?? 0;
    // Die Überlappung gehört zur **eigenen** linken Kante: sie schiebt diese Figur über
    // ihre Vorgängerin, nicht die Vorgängerin nach links.
    const links = Math.max(0, kante - (ueberlapp[i] ?? 0));
    kante = links + breite;
    return { bildId: figur.bildId, links, breite, hoehe, hebung: (figur.versatz / 100) * hoehe };
  });

  return {
    figuren: gelegt,
    breite: kante,
    // Eine gehobene Figur braucht oben Platz; eine gesenkte nicht — sie verschwindet
    // unter der Bodenlinie, und genau das ist der Zweck.
    hoehe: gelegt.reduce(
      (hoch, figur) => Math.max(hoch, figur.hoehe + Math.max(0, figur.hebung)),
      0,
    ),
  };
}

/** Eine Einfügemarke: der Platz, auf dem die aufgenommene Figur landet. */
export interface Marke {
  /** Platz in der Reihe **ohne** die aufgenommene Figur — 0 heißt „ganz nach vorn". */
  platz: number;
  links: number;
  breite: number;
}

/**
 * Dieselbe Reihe, aber mit Lücken zum Absetzen.
 *
 * Gilt nur, solange eine Figur **aufgenommen** ist. Die Marken schieben die Reihe
 * auseinander — das ist der Sprung im Bild, der beim Aufnehmen sichtbar ist und der
 * erklärt, was zu tun ist: zwischen den Figuren ist jetzt Platz, und dorthin tippt man.
 *
 * Es gibt eine Marke **mehr** als Figuren: vor der ersten und hinter der letzten.
 */
export function mitMarken(
  layout: Reihenlayout,
  markeBreite: number = MARKE_BREITE_PX,
): Reihenlayout & { marken: Marke[] } {
  const figuren = layout.figuren.map((figur, i) => ({
    ...figur,
    // Vor dieser Figur liegen i + 1 Marken: eine vor der Reihe und eine vor jeder
    // Vorgängerin.
    links: figur.links + (i + 1) * markeBreite,
  }));

  const marken: Marke[] = layout.figuren.map((figur, i) => ({
    platz: i,
    links: figur.links + i * markeBreite,
    breite: markeBreite,
  }));
  marken.push({
    platz: layout.figuren.length,
    links: layout.breite + layout.figuren.length * markeBreite,
    breite: markeBreite,
  });

  return {
    figuren,
    marken,
    breite: layout.breite + (layout.figuren.length + 1) * markeBreite,
    hoehe: layout.hoehe,
  };
}

/**
 * Die aufgenommene Figur an einem Platz absetzen.
 *
 * `platz` zählt in der Reihe **ohne** die aufgenommene Figur — genau so, wie die Marken
 * gezählt sind. Das erspart die Umrechnung „liegt das Ziel vor oder hinter der Lücke, die
 * das Herausnehmen hinterlässt", die sonst an zwei Stellen stimmen müsste.
 */
export function absetzen<T>(liste: readonly T[], von: number, platz: number): T[] {
  const kopie = [...liste];
  if (von < 0 || von >= kopie.length) return kopie;
  const [figur] = kopie.splice(von, 1);
  if (figur === undefined) return kopie;
  kopie.splice(Math.min(Math.max(0, platz), kopie.length), 0, figur);
  return kopie;
}

/** Eine Figur in der Größe ändern. Eingesperrt und gerundet, wie es gespeichert wird. */
export function mitGroesse<T extends { bildId: string; groesse: number }>(
  liste: readonly T[],
  bildId: string,
  wert: number,
): T[] {
  return liste.map((figur) =>
    figur.bildId === bildId ? { ...figur, groesse: rundeGroesse(wert) } : figur,
  );
}

/** Eine Figur senkrecht verschieben. Positiv hebt an, negativ senkt. */
export function mitVersatz<T extends { bildId: string; versatz: number }>(
  liste: readonly T[],
  bildId: string,
  wert: number,
): T[] {
  return liste.map((figur) =>
    figur.bildId === bildId ? { ...figur, versatz: rundeVersatz(wert) } : figur,
  );
}

/**
 * Die Reihe, wie sie gespeichert wird: der Platz ist der **Index**, nicht ein gemerkter
 * Wert.
 *
 * Damit ist die Zusage aus `shared/anordnung.ts` von selbst erfüllt — die Plätze sind
 * immer eine Vertauschung von 0 … n−1. Eine zweite Zählung mitzuführen wäre eine zweite
 * Wahrheit über dieselbe Reihenfolge.
 */
export function alsReihe(
  liste: readonly { bildId: string; groesse: number; versatz: number }[],
): Anordnung[] {
  return liste.map((figur, i) =>
    rundeAnordnung({
      bildId: figur.bildId,
      reihenfolge: i,
      groesse: figur.groesse,
      versatz: figur.versatz,
    }),
  );
}
